import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SubtitleFilterPanel from "@/components/feature/SubtitleFilterPanel";
import SubtitleCard from "@/components/base/SubtitleCard";
import Pagination from "@/components/base/Pagination";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import {
  allCategories,
  allSubtitles,
  applyFilters,
  defaultFilters,
  getCategoryBySlug,
  sortOptions,
  sortSubtitles,
  type SubtitleFilters,
} from "@/utils/subtitles";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const category = type ? getCategoryBySlug(type) : undefined;

  const [filters, setFilters] = useState<SubtitleFilters>(defaultFilters);
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  useDocumentTitle(
    category ? `${category.title} - 中文在线字幕免费高清` : "字幕分类 - 中文在线字幕免费高清",
    category
      ? `${category.name}专区，汇集${category.desc}支持在线预览与 SRT、ASS 格式下载。`
      : "中文在线字幕免费高清字幕分类页。",
  );

  const list = useMemo(
    () => allSubtitles.filter((item) => item.typeSlug === type),
    [type],
  );

  const resultList = useMemo(
    () => sortSubtitles(applyFilters(list, filters), sort),
    [list, filters, sort],
  );

  useEffect(() => {
    setPage(1);
  }, [type, filters, sort]);

  const totalPages = Math.ceil(resultList.length / PAGE_SIZE);
  const paged = resultList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeSort = sortOptions.find((item) => item.value === sort)?.label ?? "最新更新";

  const resetFilters = () => {
    setFilters(defaultFilters);
    setSort("latest");
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-error-warning-line text-5xl text-accent-500"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">未找到该字幕分类</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的分类可能已调整或不存在。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
          <Link
            to="/search"
            className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300"
          >
            搜索字幕
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
      <Breadcrumb items={[{ label: category.name }]} />

      <div className="mt-5 flex flex-wrap items-start justify-between gap-4 border-b border-background-200 pb-6">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${category.icon} text-3xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {category.title}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
              {category.desc}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {allCategories
            .filter((item) => item.slug !== category.slug)
            .map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className="whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {item.name}
              </Link>
            ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block">
          <SubtitleFilterPanel
            filters={filters}
            sort={sort}
            onFilterChange={setFilters}
            onSortChange={setSort}
            onReset={resetFilters}
          />
        </aside>

        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-600">{resultList.length}</span> 个字幕资源
              <span className="ml-2 text-foreground-400">· 按{activeSort}排序</span>
            </p>
            <button
              type="button"
              onClick={() => setShowMobileFilter((value) => !value)}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 lg:hidden"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>
          </div>

          {showMobileFilter && (
            <div className="mb-5 lg:hidden">
              <SubtitleFilterPanel
                filters={filters}
                sort={sort}
                onFilterChange={setFilters}
                onSortChange={setSort}
                onReset={resetFilters}
              />
            </div>
          )}

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {paged.map((item) => (
                  <SubtitleCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-emotion-sad-line text-5xl text-background-500"></i>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-800">
                抱歉，暂无符合条件的字幕资源
              </h3>
              <p className="mt-2 text-sm text-foreground-500">试试调整或重置筛选条件</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}